import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError, JobProgress } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import type { EmailAccountInfo, TestEmailRecipient } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";
import UserContext from "../../../context/UserContext";
import { getTestEmailResult } from "./testEmailResult";
import TestEmailResultAlert from "./TestEmailResultAlert";

interface Props {
  account: EmailAccountInfo;
  clientCodes: string[];
  getClientTitle: (clientCode: string) => string;
  onClose: () => void;
}

const startTestEmail = withErrorHandling(api.emailAccounts.startTestEmail);

const SendTestEmailDialog = ({ account, clientCodes, getClientTitle, onClose }: Props) => {
  const { userInfo } = useDefinedContext(UserContext);
  const [clientCode, setClientCode] = useState(clientCodes[0] ?? "");
  const [recipient, setRecipient] = useState<TestEmailRecipient>("TestInbox");
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<ApiError>();
  const [finishedProgress, setFinishedProgress] = useState<JobProgress>();

  const { jobProgress, startJobProgressCheck } = useJobProgress(api.emailAccounts.getTestEmailProgress, {
    updateIntervalMs: 3000,
    onJobFinished: setFinishedProgress,
    onPollingError: setStartError,
  });

  const isRunning = isStarting || jobProgress !== undefined;
  const displayedProgress = jobProgress ?? finishedProgress;
  const result = displayedProgress ? getTestEmailResult(displayedProgress) : undefined;

  const handleSendClick = async () => {
    setIsStarting(true);
    setStartError(undefined);
    setFinishedProgress(undefined);

    const [response, error] = await startTestEmail(clientCode, account.id, { recipient });
    setIsStarting(false);
    if (error) {
      logError(error, "[SendTestEmailDialog] startTestEmail");
      setStartError(error);
      return;
    }

    startJobProgressCheck(response.operationId);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Send test email</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>From {account.address}</Typography>
          {clientCodes.length > 1 && (
            <FormControl>
              <InputLabel id="send-test-email-client">Send as client</InputLabel>
              <Select
                labelId="send-test-email-client"
                label="Send as client"
                value={clientCode}
                disabled={isRunning}
                onChange={(event) => setClientCode(event.target.value)}
              >
                {clientCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {getClientTitle(code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <RadioGroup value={recipient} onChange={(_, value) => setRecipient(value === "Me" ? "Me" : "TestInbox")}>
            <FormControlLabel value="TestInbox" control={<Radio />} label="Entrilia test inbox" disabled={isRunning} />
            <FormControlLabel
              value="Me"
              control={<Radio />}
              label={`Me (${userInfo.signInEmail})`}
              disabled={isRunning}
            />
          </RadioGroup>
          {startError && <Alert severity="error">{startError.message}</Alert>}
          {result && <TestEmailResultAlert result={result} />}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelText="Close">
        <Button
          variant="contained"
          loading={isRunning}
          disabled={!clientCode || finishedProgress !== undefined}
          onClick={handleSendClick}
        >
          Send
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SendTestEmailDialog;
